import { Option } from "effect";
import * as AsyncData from "foldkit/asyncData";
import type { Html, HtmlBuilder } from "foldkit/html";
import * as Mount from "foldkit/mount";
import { defineView } from "foldkit/submodel";
import { tabsOf } from "../../widgets/tabs";

import type { Entry } from "../../api/schemas/entries";
import { Route, linkHref } from "../../app/routes";
import { i18next } from "../../i18n";
import {
	formatFileSize,
	localDateTimeToIso,
	localIsoDate,
	localIsoDateTime,
} from "../../widgets/addEntry";
import * as Attributes from "../../widgets/attributes";
import * as Editor from "../../widgets/editor";
import * as Icons from "../../widgets/icons";
import { markdown } from "../../widgets/markdown";
import { lastEditView, provenanceView } from "../../widgets/provenance";
import * as Tags from "../../widgets/tags";
import { parseTags, sourceBadgeClass, versionAt } from "../history";
import type { Shared } from "../shared";
import { Message } from "./message";
import type { EditForm, History, Model } from "./model";
import {
	buildRows,
	entrySchema,
	errorText,
	latestUploadFile,
	parseTasks,
	tagSuggestions,
} from "./update";

const t = (key: string, options: Record<string, unknown> = {}) =>
	i18next.t(key, options);

/** v1 `format(date, "PPpp")`; the raw string when unparsable. */
export const formatLong = (iso: string): string => {
	const d = new Date(iso);
	return Number.isNaN(d.getTime())
		? iso
		: d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "medium" });
};

/** v1 `Entry.tsx:402-410`: title, else the first 25 chars of the description, else "Untitled Entry". */
export const displayTitle = (entry: Entry): string =>
	entry.title ||
	(entry.description?.trim().split("\n")[0] ?? "").slice(0, 25).trim() ||
	t("entry.untitled");

/** v1 `entryIconMap`: lucide icon per entry type, `StickyNote` otherwise. */
const ICONS: Record<
	string,
	(h: HtmlBuilder<Message>, o?: Icons.IconOptions) => Html
> = {
	upload: Icons.FileDown,
	tasks: Icons.CheckSquare,
	update: Icons.RefreshCw,
	learning: Icons.Lightbulb,
	risk: Icons.TriangleAlert,
	decision: Icons.Scale,
	accomplishment: Icons.Trophy,
	hypothesis: Icons.CircleHelp,
	experiment: Icons.FlaskConical,
};

const card = (
	h: HtmlBuilder<Message>,
	children: ReadonlyArray<Html>,
	classes = "card bg-base-100 border border-base-300 shadow-sm mb-6",
	bodyClasses = "card-body p-4",
): Html => h.div([h.Class(classes)], [h.div([h.Class(bodyClasses)], children)]);

const spinner = (
	h: HtmlBuilder<Message>,
	classes = "loading-dots loading-lg",
): Html => h.span([h.Class(`loading ${classes}`)], []);

/** v1 `ErrorMessage`. */
const errorAlert = (h: HtmlBuilder<Message>, message: string): Html =>
	h.div(
		[
			h.DataAttribute("testid", "page-Entry"),
			h.Role("alert"),
			h.Class("alert alert-error"),
		],
		[
			Icons.AlertCircle(h, { class: "h-6 w-6 shrink-0 stroke-current" }),
			h.span([], [message]),
		],
	);

// HEADER

const linkedRecords = (
	h: HtmlBuilder<Message>,
	model: Model,
	shared: Shared,
	entry: Entry,
): Html => {
	if (entry.nodeIds.length === 0) return h.empty;
	const records = AsyncData.getOrElse(model.linkedRecords, () => null);
	return h.div(
		[
			h.Class(
				"mt-1 flex flex-wrap items-center gap-2 text-sm text-base-content/70",
			),
		],
		[
			Icons.User(h, { size: 14 }),
			...(AsyncData.isLoading(model.linkedRecords)
				? [spinner(h, "loading-spinner loading-xs")]
				: records !== null && records.length > 0
					? [
							h.span([h.Class("text-base-content/60")], [t("entry.linkedTo")]),
							...records.map((record) =>
								h.keyed("a")(
									record.id,
									[
										h.Href(
											linkHref(
												shared.route,
												Route.Record({ id: record.id, asOf: Option.none() }),
											),
										),
										h.Class(
											"badge badge-outline badge-sm transition-colors hover:border-primary hover:text-primary",
										),
									],
									[record.name || t("entry.untitledRecord")],
								),
							),
							entry.nodeIds.length > records.length
								? h.span(
										[h.Class("text-xs text-base-content/60")],
										[
											t("entry.more", {
												count: entry.nodeIds.length - records.length,
											}),
										],
									)
								: h.empty,
						]
					: [
							h.span(
								[],
								[t("entry.linkedToRecords", { count: entry.nodeIds.length })],
							),
						]),
		],
	);
};

const tagBadges = (
	h: HtmlBuilder<Message>,
	tags: ReadonlyArray<string>,
): Html =>
	h.div(
		[h.Class("flex flex-wrap gap-1")],
		tags.map((tag) =>
			h.keyed("span")(tag, [h.Class("badge badge-outline badge-sm")], [tag]),
		),
	);

const tagsSection = (
	h: HtmlBuilder<Message>,
	shared: Shared,
	entry: Entry,
	edit: Option.Option<EditForm>,
): Html => {
	const unique = Array.from(
		new Set(entry.tags.filter((tag) => tag.trim() !== "")),
	);
	if (Option.isNone(edit) && unique.length === 0) return h.empty;
	return h.div(
		[h.Class("mt-3 space-y-2 text-sm text-base-content/80")],
		[
			h.div(
				[h.Class("flex items-center gap-2")],
				[
					Icons.Hash(h, { size: 14, class: "text-base-content/60" }),
					h.span([h.Class("font-semibold")], [t("entry.tags")]),
					Option.isNone(edit) && unique.length === 0
						? h.span([h.Class("text-base-content/60")], [t("entry.noTags")])
						: h.empty,
				],
			),
			Option.match(edit, {
				onNone: () => tagBadges(h, unique),
				onSome: (form) =>
					Tags.view(h, {
						slotId: "edit-entry-tags",
						model: form.tagBox,
						selected: form.tags,
						suggestions: tagSuggestions(shared),
						ariaLabel: t("entry.editTags"),
						placeholder: t("entry.addTag"),
						error: form.tagError,
						toParentMessage: (message) => Message.GotTagsMessage({ message }),
						onRemove: (tag) => Message.ClickedRemoveTag({ tag }),
					}),
			}),
		],
	);
};

const editButtons = (
	h: HtmlBuilder<Message>,
	model: Model,
	shared: Shared,
	entry: Entry,
): Html => {
	if (entry.type === "upload" || Option.isSome(shared.asOf)) return h.empty;
	return h.div(
		[h.Class("flex items-center gap-2")],
		Option.isSome(model.edit)
			? [
					h.button(
						[
							h.Type("button"),
							h.Class("btn btn-primary btn-sm"),
							h.AriaLabel(t("entry.save")),
							h.Disabled(model.saving),
							h.OnClick(Message.ClickedSave()),
						],
						[
							model.saving
								? spinner(h, "loading-spinner loading-xs")
								: t("entry.save"),
						],
					),
					h.button(
						[
							h.Type("button"),
							h.Class("btn btn-ghost btn-sm"),
							h.AriaLabel(t("entry.cancel")),
							h.Disabled(model.saving),
							h.OnClick(Message.ClickedCancelEdit()),
						],
						[t("entry.cancel")],
					),
				]
			: [
					h.button(
						[
							h.Type("button"),
							h.Class("btn btn-ghost btn-sm"),
							h.AriaLabel(t("entry.edit")),
							h.OnClick(Message.ClickedEdit()),
						],
						[t("entry.edit")],
					),
				],
	);
};

const header = (
	h: HtmlBuilder<Message>,
	model: Model,
	shared: Shared,
	entry: Entry,
): Html =>
	h.div(
		[h.Class("mb-6")],
		[
			h.div(
				[
					h.Class(
						"flex flex-col gap-4 md:flex-row md:items-start md:justify-between",
					),
				],
				[
					h.div(
						[h.Class("flex items-start gap-3")],
						[
							(ICONS[entry.type] ?? Icons.StickyNote)(h, {
								size: 24,
								class: "text-primary mt-1",
							}),
							h.div(
								[h.Class("w-full")],
								[
									Option.match(model.edit, {
										onNone: () =>
											h.h1(
												[h.Class("text-3xl font-bold text-base-content")],
												[displayTitle(entry)],
											),
										onSome: (form) =>
											h.input([
												h.Type("text"),
												h.Class(
													"input input-bordered w-full text-2xl font-semibold",
												),
												h.AriaLabel("Title"),
												h.Placeholder(t("entry.untitled")),
												h.Value(form.title),
												h.OnInput((value) => Message.ChangedTitle({ value })),
											]),
									}),
									// R16, R17: who wrote the entry, and who last edited it.
									h.div(
										[
											h.Class(
												"mt-1 flex flex-wrap items-center gap-3 text-xs text-base-content/60",
											),
											h.DataAttribute("testid", "entry-provenance"),
										],
										[
											provenanceView(h, entry.provenance, entry.author),
											lastEditView(h, entry.lastEdit),
										],
									),
									linkedRecords(h, model, shared, entry),
									tagsSection(h, shared, entry, model.edit),
								],
							),
						],
					),
					editButtons(h, model, shared, entry),
				],
			),
		],
	);

/** R1: the occurred-on date and time, editable in edit mode as local wall time; the view converts it to a UTC instant. */
const occurredOnField = (
	h: HtmlBuilder<Message>,
	model: Model,
	entry: Entry,
): Html =>
	h.div(
		[h.Class("flex items-center gap-2")],
		[
			Icons.Calendar(h, { size: 16, class: "text-base-content/60" }),
			h.label(
				[h.For("entry-occurred-on"), h.Class("text-base-content/60")],
				[t("entry.occurredOn")],
			),
			Option.match(model.edit, {
				onNone: () =>
					h.span(
						[h.Id("entry-occurred-on"), h.Class("text-base-content/80")],
						[formatLong(entry.timestamp)],
					),
				onSome: (form) =>
					h.input([
						h.Id("entry-occurred-on"),
						h.Type("datetime-local"),
						h.Class("input input-bordered input-sm"),
						h.Max(`${localIsoDate(new Date())}T23:59`),
						h.Value(localIsoDateTime(new Date(form.timestamp))),
						h.OnChange((value) =>
							Message.ChangedOccurredOn({
								value: localDateTimeToIso(value) ?? "",
							}),
						),
					]),
			}),
		],
	);

// ATTRIBUTES (v1 AttributeEditor)

const toAttributeMessage = (message: Attributes.Message): Message =>
	Attributes.Message.match<Message>(message, {
		ClickedAdd: () => Message.ClickedAddAttribute(),
		ChangedKey: ({ index, value }) =>
			Message.ChangedAttributeKey({ index, value }),
		ChangedType: ({ index, value }) =>
			Message.ChangedAttributeType({ index, value }),
		ChangedValue: ({ index, value }) =>
			Message.ChangedAttributeValue({ index, value }),
		ClickedRemove: ({ index }) => Message.ClickedRemoveAttribute({ index }),
	});

const attributesCard = (
	h: HtmlBuilder<Message>,
	model: Model,
	shared: Shared,
	entry: Entry,
): Html =>
	Option.match(model.edit, {
		// v1 builds the read-only rows through the same schema pass, so schema types apply.
		onNone: () => {
			const rows = buildRows(entry.attributes, entrySchema(shared)).filter(
				(row) => row.value.value.trim() !== "",
			);
			return rows.length === 0
				? h.empty
				: card(h, [
						h.h2(
							[h.Class("text-base-content/80 font-semibold")],
							[t("entry.attributes")],
						),
						h.div([h.Class("mt-2")], [Attributes.readOnlyView(h, rows)]),
					]);
		},
		onSome: (form) =>
			card(h, [
				h.h2(
					[h.Class("text-base-content/80 font-semibold")],
					[t("entry.attributes")],
				),
				h.div(
					[h.Class("mt-2")],
					[
						Attributes.view(h, {
							rows: form.attributes,
							schema: entrySchema(shared),
							toParentMessage: toAttributeMessage,
						}),
					],
				),
			]),
	});

// FILE (upload entries)

const fileCard = (
	h: HtmlBuilder<Message>,
	model: Model,
	entry: Entry,
): Html => {
	if (entry.type !== "upload") return h.empty;
	const file = latestUploadFile(entry.json);
	return card(
		h,
		[
			h.h2(
				[h.Class("card-title text-base-content/80 mb-4")],
				[t("entry.file")],
			),
			file === undefined
				? h.p([h.Class("text-sm text-error")], [t("entry.fileUnavailable")])
				: h.div(
						[h.Class("flex flex-col gap-3")],
						[
							h.div(
								[],
								[
									h.div(
										[h.Class("font-medium text-base-content")],
										[file.name],
									),
									h.div(
										[h.Class("text-sm text-base-content/70")],
										[
											`${formatFileSize(file.size)}${file.uploadedAt ? ` • ${t("entry.uploaded")} ${formatLong(file.uploadedAt)}` : ""}`,
										],
									),
								],
							),
							entry.description
								? h.p(
										[h.Class("text-sm text-base-content/80")],
										[entry.description],
									)
								: h.empty,
							h.div(
								[h.Class("flex items-center gap-2")],
								[
									h.button(
										[
											h.Type("button"),
											h.Class("btn btn-primary btn-sm"),
											h.AriaLabel(t("entry.download")),
											h.Disabled(model.downloading),
											h.OnClick(Message.ClickedDownload()),
										],
										[
											model.downloading
												? spinner(h, "loading-spinner loading-xs")
												: t("entry.download"),
										],
									),
									Option.match(model.downloadError, {
										onNone: () => h.empty,
										onSome: (error) =>
											h.span(
												[h.Role("alert"), h.Class("text-sm text-error")],
												[error],
											),
									}),
								],
							),
						],
					),
		],
		undefined,
		"card-body",
	);
};

// CONTENT

const checklist = (
	h: HtmlBuilder<Message>,
	shared: Shared,
	entry: Entry,
): Html => {
	const tasks = parseTasks(entry.json);
	if (tasks.length === 0)
		return h.div([h.Class("text-base-content/60 italic")], ["No tasks"]);
	const editable = Option.isNone(shared.asOf);
	return h.div(
		[h.Class("space-y-2")],
		tasks.map((task) =>
			h.keyed("div")(
				task.id,
				[h.Class("flex items-center gap-2")],
				[
					h.input([
						h.Type("checkbox"),
						h.Class(
							`checkbox ${editable ? "checkbox-primary" : "checkbox-disabled"}`,
						),
						h.AriaLabel(task.text),
						h.Checked(task.completed),
						h.Disabled(!editable),
						h.OnClick(Message.ClickedToggleTask({ taskId: task.id })),
					]),
					h.span(
						[
							h.Class(
								`flex-1 ${task.completed ? "line-through text-base-content/60" : "text-base-content"}`,
							),
						],
						[task.text],
					),
				],
			),
		),
	);
};

const editor = (
	h: HtmlBuilder<Message>,
	model: Model,
	shared: Shared,
	form: EditForm,
): Html =>
	h.div(
		[h.Class("rounded-lg border border-base-300")],
		[
			form.editorFocused
				? Editor.toolbar(h, (action) => Message.ClickedEditorAction({ action }))
				: h.empty,
			h.div([
				h.Key(model.knotId),
				h.DataAttribute("testid", "editor-host"),
				h.OnMount(
					Mount.mapMessage(
						Editor.MarkdownEditor({
							id: model.knotId,
							initialText: form.description,
							placeholder: t("entry.addDescription"),
							dark: shared.theme === "dark",
							minHeight: 200,
						}),
						(message) => Message.GotEditorMessage({ message }),
					),
				),
			]),
		],
	);

const contentCard = (
	h: HtmlBuilder<Message>,
	model: Model,
	shared: Shared,
	entry: Entry,
): Html =>
	card(
		h,
		[
			h.h2(
				[h.Class("card-title text-base-content/80 mb-4")],
				[t("common.content")],
			),
			entry.type === "upload"
				? h.p(
						[h.Class("text-base-content/80")],
						[entry.description || t("entry.noContent")],
					)
				: entry.type === "tasks"
					? checklist(h, shared, entry)
					: Option.match(model.edit, {
							onSome: (form) => editor(h, model, shared, form),
							onNone: () =>
								entry.description !== undefined &&
								entry.description === entry.title
									? h.p(
											[h.Class("text-base-content/70 italic")],
											[t("entry.descriptionSameAsTitle")],
										)
									: h.div(
											[h.DataAttribute("testid", "markdown-preview")],
											[markdown(h, entry.description || t("entry.noContent"))],
										),
						}),
		],
		"card bg-base-100 border border-base-300 shadow-sm",
		"card-body",
	);

const additionalData = (
	h: HtmlBuilder<Message>,
	model: Model,
	entry: Entry,
): Html => {
	if (!entry.json || entry.json.trim() === "") return h.empty;
	let pretty = entry.json;
	try {
		pretty = JSON.stringify(JSON.parse(entry.json), null, 2);
	} catch {
		// v1 throws here; v2 shows the raw text instead.
	}
	return h.div(
		[h.Class("card bg-base-100 border border-base-300 mt-6 shadow-sm")],
		[
			h.div(
				[h.Class("card-body")],
				[
					h.button(
						[
							h.Type("button"),
							h.Class(
								"flex w-full items-center justify-between text-left text-base-content/80",
							),
							h.AriaLabel(t("entry.additionalData")),
							h.AriaExpanded(model.additionalDataOpen),
							h.AriaControls("entry-additional-data-panel"),
							h.OnClick(Message.ToggledAdditionalData()),
						],
						[
							h.span([h.Class("font-semibold")], [t("entry.additionalData")]),
							Icons.ChevronDown(h, {
								size: 16,
								class: `transition-transform${model.additionalDataOpen ? " rotate-180" : ""}`,
							}),
						],
					),
					model.additionalDataOpen
						? h.pre(
								[
									h.Id("entry-additional-data-panel"),
									h.Class(
										"bg-base-200 p-4 rounded-lg text-sm overflow-x-auto mt-4",
									),
								],
								[h.code([], [pretty])],
							)
						: h.empty,
				],
			),
		],
	);
};

// HISTORY (v1 EntryHistoryTab + HistoryVersionCard)

/** Date and time in the user's locale; the raw string when unparsable. */
export const formatShort = (iso: string): string => {
	const d = new Date(iso);
	return Number.isNaN(d.getTime())
		? iso
		: d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
};

const historyView = (h: HtmlBuilder<Message>, model: Model): Html => {
	if (AsyncData.isLoading(model.history)) {
		return h.div(
			[h.Role("status"), h.Class("w-full text-center py-8")],
			[spinner(h)],
		);
	}
	if (AsyncData.isFailure(model.history)) {
		return h.div(
			[h.Role("alert"), h.Class("text-center py-8 text-error")],
			[t("history.failedToLoad")],
		);
	}
	const history: History = AsyncData.getOrElse(model.history, () => ({
		items: [],
		nextCursor: Option.none(),
	}));
	if (history.items.length === 0) {
		return h.div(
			[h.Class("text-center py-8 text-base-content/60")],
			[t("history.noHistory")],
		);
	}
	const line = (label: string, value: Html | string) =>
		h.div(
			[],
			[h.span([h.Class("font-semibold")], [`${t(label)}:`]), " ", value],
		);
	return h.div(
		[h.Class("flex flex-col gap-3")],
		[
			...history.items.map((entry, index) => {
				const version = versionAt(history.items.length, index);
				const cardId = `history-version-${version}`;
				const expanded = model.expandedVersions.includes(version);
				const data =
					typeof entry.version.data === "object" && entry.version.data !== null
						? (entry.version.data as Record<string, unknown>)
						: {};
				const title = entry.version.name ?? entry.version.title;
				const createdBy =
					typeof data.createdBy === "string"
						? data.createdBy
						: entry.version.createdBy;
				const tags = parseTags(entry.version.tags);
				return h.keyed("div")(
					`${entry.validFrom}-${index}`,
					[],
					[
						h.div(
							[h.Class("card bg-base-100 border border-base-300 shadow-sm")],
							[
								h.button(
									[
										h.Type("button"),
										h.Class("card-body p-4 cursor-pointer text-left w-full"),
										h.AriaExpanded(expanded),
										h.AriaControls(`${cardId}-content`),
										h.OnClick(Message.ToggledHistoryVersion({ version })),
									],
									[
										h.div(
											[h.Class("flex items-center justify-between")],
											[
												h.div(
													[h.Class("flex items-center gap-2")],
													[
														h.span(
															[h.Class("font-semibold text-base-content")],
															[`${t("history.version")} ${version}`],
														),
														entry.validTo === null
															? h.span(
																	[h.Class("badge badge-success badge-sm")],
																	[t("history.current")],
																)
															: h.empty,
														h.span(
															[
																h.Class(
																	`badge badge-sm ${sourceBadgeClass(entry.event.source)}`,
																),
															],
															[entry.event.source],
														),
														// R19: the same label and popover per version.
														provenanceView(h, entry.event.provenance, null, {
															channelBadge: false,
														}),
													],
												),
												h.div(
													[h.Class("flex items-center gap-2")],
													[
														h.span(
															[h.Class("text-sm text-base-content/60")],
															[formatShort(entry.validFrom)],
														),
														Icons.ChevronDown(h, {
															size: 16,
															class: `transition-transform${expanded ? " rotate-180" : ""}`,
														}),
													],
												),
											],
										),
										entry.validTo
											? h.div(
													[h.Class("text-xs text-base-content/50 mt-1")],
													[
														`${t("history.validUntil")} ${formatShort(entry.validTo)}`,
													],
												)
											: h.empty,
									],
								),
								expanded
									? h.div(
											[h.Id(`${cardId}-content`), h.Class("px-4 pb-4")],
											[
												h.div([h.Class("divider my-0")], []),
												h.div(
													[h.Class("mt-3")],
													[
														h.div(
															[h.Class("space-y-2 text-sm")],
															[
																title
																	? line("history.title_label", title)
																	: h.empty,
																line(
																	"history.type",
																	h.span(
																		[h.Class("capitalize")],
																		[entry.version.type],
																	),
																),
																line(
																	"history.description",
																	entry.version.description ?? "",
																),
																tags.length > 0
																	? h.div(
																			[
																				h.Class(
																					"flex flex-wrap items-center gap-1",
																				),
																			],
																			[
																				h.span(
																					[h.Class("font-semibold")],
																					[`${t("history.tags")}:`],
																				),
																				...tags.map((tag) =>
																					h.keyed("span")(
																						tag,
																						[
																							h.Class(
																								"badge badge-outline badge-xs",
																							),
																						],
																						[tag],
																					),
																				),
																			],
																		)
																	: h.empty,
																createdBy
																	? line("history.createdBy", createdBy)
																	: h.empty,
															],
														),
													],
												),
												h.div(
													[h.Class("mt-3")],
													[
														h.button(
															[
																h.Type("button"),
																h.Class("btn btn-ghost btn-xs"),
																h.AriaLabel(
																	`View app state at ${formatLong(entry.validFrom)}`,
																),
																h.OnClick(
																	Message.ClickedViewAt({
																		validFrom: entry.validFrom,
																	}),
																),
															],
															[t("history.viewAtThisPoint")],
														),
													],
												),
											],
										)
									: h.empty,
							],
						),
					],
				);
			}),
			Option.isSome(history.nextCursor)
				? h.button(
						[
							h.Type("button"),
							h.Class("btn btn-sm self-center"),
							h.AriaLabel("Load more history"),
							h.OnClick(Message.ClickedLoadMoreHistory()),
						],
						["Load more"],
					)
				: h.empty,
		],
	);
};

export const view = defineView<Model, Message, Shared>((model, shared, h) => {
	const error = errorText(model, shared);
	const tabs = tabsOf(h, model.tab, (tab) => Message.SelectedTab({ tab }));
	if (error !== undefined) {
		return errorAlert(h, error);
	}
	const entry = AsyncData.getOrElse(model.entry, () => null);
	if (entry === null) {
		return h.div(
			[
				h.DataAttribute("testid", "page-Entry"),
				h.Role("status"),
				h.Class("w-full text-center"),
			],
			[spinner(h)],
		);
	}
	return h.div(
		[
			h.DataAttribute("testid", "page-Entry"),
			h.Class("max-w-4xl mx-auto px-4 py-8"),
		],
		[
			h.div(
				[h.Class("mb-4")],
				[
					h.button(
						[
							h.Type("button"),
							h.Class("btn btn-ghost btn-sm"),
							h.AriaLabel(t("common.back")),
							h.OnClick(Message.ClickedBack()),
						],
						[Icons.ArrowLeft(h, { size: 16 }), t("common.back")],
					),
				],
			),
			tabs.list([
				tabs.tab("content", t("common.content")),
				tabs.panel("content", () =>
					h.div(
						[h.Class("card bg-base-100 border border-base-300 shadow-sm")],
						[
							h.div(
								[h.Class("card-body p-6 md:p-8")],
								[
									header(h, model, shared, entry),
									card(h, [
										h.h2(
											[h.Class("text-base-content/80 font-semibold")],
											[t("entry.metadata")],
										),
										h.div(
											[
												h.Class(
													"mt-4 grid grid-cols-1 gap-4 text-sm md:grid-cols-2",
												),
											],
											[
												occurredOnField(h, model, entry),
												h.div(
													[h.Class("flex items-center gap-2")],
													[
														Icons.Hash(h, {
															size: 16,
															class: "text-base-content/60",
														}),
														h.span(
															[h.Class("text-base-content/80 capitalize")],
															[entry.type],
														),
													],
												),
											],
										),
									]),
									attributesCard(h, model, shared, entry),
									fileCard(h, model, entry),
									contentCard(h, model, shared, entry),
									additionalData(h, model, entry),
								],
							),
						],
					),
				),
				tabs.tab("history", t("common.history")),
				tabs.panel("history", () => historyView(h, model)),
			]),
		],
	);
});
